import { jsx as _jsx } from "react/jsx-runtime";
import { AdjacentType, Assembler, getIndirectGroupedTypeQuery, getItemTypeToGroupNestedDescendingQueryDef, sortByName, pluralize, } from '@iotv/datamodel';
import { Autocomplete, CircularProgress, FormControl, InputAdornment } from '@mui/material';
import { useEffect, useState } from 'react';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { camelCase_to_Sentence, singularArticle } from '../../helpers/format';
import { AutosizedPopper } from '../common/AutosizedPopper';
import { AutosizedTextField } from '../common/AutosizedTextField';
import config from '@iotv/config';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Queries either the database or (provided) application state relative to a customer or user, respectively, for a group supporting a given entity type
 * Provides a UI element to select a group
 *
 * @param contextObject either a `contextCustomer` to query relative to, or a `contextUser` to query relative to their allowed groups
 * @param entityType type of the entity group to query for
 * @param applicationData only required if passing a `contextUser`, to use for querying
 * @param currentGroup the currently selected group, should be from `React.useState()`
 * @param setCurrentGroup a function to set the currently selected group, should be from `React.useState()`
 * @param label a label to apply to the textfield
 * @param fullWidth whether to render to the full width of the parent container, defaults to true
 * @param disabled whether the control is disabled, defaults to false
 * @param required whether the control should hint that a value is required, defaults to false
 * @returns Mui {@link Autocomplete}
 */
export const SingleGroupAutoQuery = ({ contextObject, entityType, applicationData, currentGroup, setCurrentGroup, label, fullWidth = true, disabled = false, required = false, }) => {
    const [items, setItems] = useState([]);
    const [querying, setQuerying] = useState(false);
    const query = async () => {
        if (contextObject && entityType) {
            try {
                setQuerying(true);
                if (contextObject.type === 'Customer') {
                    debug &&
                        console.log('SingleGroupAutoQuery querying with Customer', {
                            contextObject,
                        });
                    const query = getItemTypeToGroupNestedDescendingQueryDef({
                        groupDefiningObjectTypeId: 'Customer',
                        supportedType: entityType,
                        includeSupportedTypeInstances: true,
                    });
                    const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
                        contextObjects: [contextObject],
                        query,
                    });
                    if (!err && data)
                        setItems(data
                            .getNodesOfType('Group')
                            .map((zombie) => Assembler.getInstance(zombie))
                            .filter((g) => g !== undefined)
                            .filter((g) => g.supportedType === entityType)
                            .sort(sortByName));
                    setQuerying(false);
                }
                else if (contextObject.type === 'User' && applicationData) {
                    debug &&
                        console.log('SingleGroupAutoQuery querying with User', {
                            contextObject,
                        });
                    const allowedGroupsQuery = getIndirectGroupedTypeQuery({
                        querySubjects: [contextObject],
                        querySubjectType: 'User',
                        allowedGroupSupportedType: entityType,
                    });
                    const allowedGroups = allowedGroupsQuery(applicationData);
                    console.log('SingleGroupAutoQuery allowed groups', allowedGroups);
                    if (allowedGroups) {
                        const response = await ConsumerDao.getGraphQueryResponse({
                            contextObjects: allowedGroups,
                            path: [{ objectTypeId: entityType, adjacencyType: AdjacentType.CHILD }],
                        });
                        if (response.data?.lastNodesAndEdges.nodes) {
                            setItems(response.data?.lastNodesAndEdges.nodes
                                .map((zombie) => Assembler.getInstance(zombie))
                                .filter((v) => v !== undefined)
                                .sort(sortByName));
                        }
                        debug && console.log('SingleGroupAutoQuery query results', response);
                        setQuerying(false);
                    }
                }
            }
            catch (e) {
                debug && console.log('Error in SingleGroupAutoQuery query', e);
                setQuerying(false);
            }
        }
        else {
            setQuerying(false);
        }
    };
    useEffect(() => {
        debug &&
            console.log('SingleGroupAutoQuery used effect on entityType', {
                entityType,
            });
        setCurrentGroup(null);
        void query();
    }, [entityType]);
    const autoCompleteLabel = label ??
        `${config.app.webUI.groupedObjectTypes?.[entityType]?.pluralCollectionName ?? pluralize(entityType ?? 'Group')} (${querying ? '...' : items.length})`;
    const InputProps = querying
        ? {
            ['InputProps']: {
                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { color: "inherit", size: 18 }) })),
            },
        }
        : undefined;
    return (_jsx(FormControl, { fullWidth: fullWidth, children: _jsx(Autocomplete, { value: currentGroup ?? '',
            id: 'selectGroup',
            size: 'small',
            disabled,
            fullWidth,
            disableClearable: true,
            forcePopupIcon: querying ? false : 'auto',
            loading: querying,
            options: items,
            freeSolo: false,
            getOptionLabel: (option) => option.name ?? '',
            isOptionEqualToValue: (option, value) => value.sk ? option.sk === value.sk : option === value,
            getOptionKey: (option) => `${option.name}${option.sk}`,
            onChange: (e, v) => {
                setCurrentGroup(v);
            },
            slots: {
                popper: AutosizedPopper,
            },
            renderInput: (params) => {
                return (_jsx(AutosizedTextField, { ...params,
                    ...InputProps,
                    label: autoCompleteLabel,
                    placeholder: querying
                        ? `Loading ${camelCase_to_Sentence(entityType ?? '', false).toLocaleLowerCase() ?? ''} groups`
                        : `Select ${singularArticle(entityType ?? 'entity')} ${camelCase_to_Sentence(entityType ?? '', false).toLocaleLowerCase() ?? ''} group`,
                    disabled,
                    error: required && !currentGroup }));
            } }) }));
};
/**
 * Queries either the database or (provided) application state relative to a customer or user, respectively, for a group supporting a given entity type
 * Provides a UI element to select some groups
 *
 * @param contextObject either a `contextCustomer` to query relative to, or a `contextUser` to query relative to their allowed groups
 * @param entityType type of the entity group to query for
 * @param applicationData only required if passing a `contextUser`, to use for querying
 * @param currentGroups the currently selected group, should be from `React.useState()`
 * @param setCurrentGroups a function to set the currently selected group, should be from `React.useState()`
 * @param onAdd a function that is called when a group is selected
 * @param onRemove a function that is called when a group is removed
 * @param label a label to apply to the textfield
 * @param fullWidth whether to render to the full width of the parent container, defaults to true
 * @param disabled whether the control is disabled, defaults to false
 * @param required whether the control should hint that a value is required, defaults to false
 * @returns Mui {@link Autocomplete}
 */
export const MultipleGroupAutoQuery = ({ contextObject, entityType, applicationData, currentGroups, setCurrentGroups, onAdd, onRemove, label, fullWidth = true, disabled = false, required = false, }) => {
    const [items, setItems] = useState([]);
    const [querying, setQuerying] = useState(false);
    const query = async () => {
        if (contextObject && entityType) {
            try {
                setQuerying(true);
                if (contextObject.type === 'Customer') {
                    debug &&
                        console.log('MultipleGroupAutoQuery querying with Customer', {
                            contextObject,
                        });
                    const query = getItemTypeToGroupNestedDescendingQueryDef({
                        groupDefiningObjectTypeId: 'Customer',
                        supportedType: entityType,
                        includeSupportedTypeInstances: true,
                    });
                    const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
                        contextObjects: [contextObject],
                        query,
                    });
                    if (!err && data)
                        setItems(data
                            .getNodesOfType('Group')
                            .map((zombie) => Assembler.getInstance(zombie))
                            .filter((g) => g !== undefined)
                            .filter((g) => g.supportedType === entityType)
                            .sort(sortByName));
                    setQuerying(false);
                }
                else if (contextObject.type === 'User' && applicationData) {
                    debug &&
                        console.log('MultipleGroupAutoQuery querying with User', {
                            contextObject,
                        });
                    const allowedGroupsQuery = getIndirectGroupedTypeQuery({
                        querySubjects: [contextObject],
                        querySubjectType: 'User',
                        allowedGroupSupportedType: entityType,
                    });
                    const allowedGroups = allowedGroupsQuery(applicationData);
                    console.log('MultipleGroupAutoQuery allowed groups', allowedGroups);
                    if (allowedGroups) {
                        const response = await ConsumerDao.getGraphQueryResponse({
                            contextObjects: allowedGroups,
                            path: [{ objectTypeId: entityType, adjacencyType: AdjacentType.CHILD }],
                        });
                        if (response.data?.lastNodesAndEdges.nodes) {
                            setItems(response.data?.lastNodesAndEdges.nodes
                                .map((zombie) => Assembler.getInstance(zombie))
                                .filter((v) => v !== undefined)
                                .sort(sortByName));
                        }
                        debug && console.log('MultipleGroupAutoQuery query results', response);
                        setQuerying(false);
                    }
                }
            }
            catch (e) {
                debug && console.log('Error in MultipleGroupAutoQuery query', e);
                setQuerying(false);
            }
        }
        else {
            setQuerying(false);
        }
    };
    useEffect(() => {
        debug &&
            console.log('MultipleGroupAutoQuery used effect on entityType', {
                entityType,
            });
        query();
    }, [entityType]);
    const autoCompleteLabel = label ?? `${pluralize(entityType ?? 'Group')} (${querying ? '...' : items.length})`;
    const InputProps = querying
        ? {
            ['InputProps']: {
                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { color: "inherit", size: 18 }) })),
            },
        }
        : undefined;
    return (_jsx(FormControl, { fullWidth: fullWidth, children: _jsx(Autocomplete, { value: currentGroups ?? '',
            id: 'selectGroups',
            size: 'small',
            disabled,
            fullWidth,
            forcePopupIcon: querying ? false : 'auto',
            loading: querying,
            options: items,
            multiple: true,
            disableCloseOnSelect: true,
            freeSolo: false,
            getOptionLabel: (option) => option.name ?? '',
            isOptionEqualToValue: (option, value) => value.sk ? option.sk === value.sk : option === value,
            getOptionKey: (option) => `${option.name}${option.sk}`,
            onChange: (e, v, r, d) => {
                switch (r) {
                    case 'selectOption':
                        d && d.option && onAdd?.(d?.option);
                        break;
                    case 'removeOption':
                        d && d.option && onRemove?.(d?.option);
                        break;
                }
                debug && console.log('MultipleGroupAutoQuery onChange', { e, v, r, d });
                setCurrentGroups(v);
            },
            slots: {
                popper: AutosizedPopper,
            },
            renderInput: (params) => {
                return (_jsx(AutosizedTextField, { ...params,
                    ...InputProps,
                    label: autoCompleteLabel,
                    placeholder: querying
                        ? `Loading ${camelCase_to_Sentence(entityType ?? '', false).toLocaleLowerCase() ?? ''} groups`
                        : `Select several ${camelCase_to_Sentence(entityType ?? '', false).toLocaleLowerCase() ?? ''} groups`,
                    disabled,
                    error: required && currentGroups.length === 0 }));
            } }) }));
};
